import { PlaceResult } from "common/interfaces/PlaceResult";
import { getAreas } from "./jsonMasters/areas";
import { getCities } from "./jsonMasters/cities";
import { getPrefectures } from "./jsonMasters/prefectures";

const PLACE_DETAIL_FIELDS = [
  "location",
  "displayName",
  "formattedAddress",
  "addressComponents",
  "websiteURI",
  "nationalPhoneNumber"
] as const;

type AddressComponentLike = {
  longText?: string | null;
  long_name?: string;
  types?: string[];
};

const componentLongText = (component: AddressComponentLike) =>
  component.longText ?? component.long_name ?? "";

export const toLatLngFromPlaceLocation = (
  location: google.maps.LatLng | google.maps.LatLngLiteral | null | undefined
): google.maps.LatLng | null => {
  if (!location) {
    return null;
  }

  if (location instanceof google.maps.LatLng) {
    return location;
  }

  return new google.maps.LatLng(location.lat, location.lng);
};

export const emptyPlaceResult = (): PlaceResult => ({
  location: null
});

export const placeResultFromPlace = (
  place: google.maps.places.Place
): PlaceResult | null => {
  const location = toLatLngFromPlaceLocation(place.location);

  if (!location) {
    return null;
  }

  return {
    location,
    displayName: place.displayName ?? undefined
  };
};

export const placeResultFromGeocoderResult = (
  result: google.maps.GeocoderResult
): PlaceResult | null => {
  const location = result.geometry?.location;

  if (!location) {
    return null;
  }

  return {
    location,
    displayName: result.formatted_address
  };
};

const loadGeocoder = async () => {
  const { Geocoder } = (await google.maps.importLibrary(
    "geocoding"
  )) as google.maps.GeocodingLibrary;

  return new Geocoder();
};

export const reverseGeocode = async (
  lat: number,
  lng: number
): Promise<google.maps.GeocoderResult | null> => {
  try {
    const geocoder = await loadGeocoder();
    const { results } = await geocoder.geocode({
      location: { lat, lng },
      language: "ja",
      region: "jp"
    });

    return results?.[0] ?? null;
  } catch {
    return null;
  }
};

const loadPlaceClass = async () => {
  const { Place } = (await google.maps.importLibrary(
    "places"
  )) as google.maps.PlacesLibrary;

  return Place;
};

const ensurePlaceFields = async (place: google.maps.places.Place) => {
  const missingFields = PLACE_DETAIL_FIELDS.filter((field) => {
    if (field === "location") {
      return !place.location;
    }

    if (field === "addressComponents") {
      return !place.addressComponents?.length;
    }

    return place[field] === undefined || place[field] === null;
  });

  if (missingFields.length > 0) {
    await place.fetchFields({ fields: [...missingFields] });
  }

  return place;
};

export const searchPlaceFromQuery = async (
  textQuery: string,
  map?: google.maps.Map | null
): Promise<google.maps.places.Place | null> => {
  if (!textQuery.trim()) {
    return null;
  }

  try {
    const Place = await loadPlaceClass();
    const center = map?.getCenter();

    const { places } = await Place.searchByText({
      textQuery,
      fields: [...PLACE_DETAIL_FIELDS],
      language: "ja",
      region: "jp",
      maxResultCount: 1,
      ...(center && { locationBias: center })
    });

    if (!places?.[0]) {
      return null;
    }

    return ensurePlaceFields(places[0]);
  } catch {
    return null;
  }
};

export const searchPlaceFromAddress = async (
  address: string
): Promise<google.maps.places.Place | null> => {
  if (!address.trim()) {
    return null;
  }

  return searchPlaceFromQuery(address);
};

const parseAddressComponents = async (
  addressComponents: AddressComponentLike[],
  options: {
    formattedAddress?: string | null;
    location?: google.maps.LatLng;
    name?: string;
    tel?: string | null;
    url?: string | null;
    regulateArea?: boolean;
  }
) => {
  const country = addressComponents.find((x) => x.types?.includes("country"));

  const postalCode = addressComponents.find((x) =>
    x.types?.includes("postal_code")
  );

  const prefecture = addressComponents.find((x) =>
    x.types?.includes("administrative_area_level_1")
  );

  const district = addressComponents.find((x) =>
    x.types?.includes("administrative_area_level_2")
  );

  const city1 = addressComponents.find((x) => x.types?.includes("locality"));

  const city2 = addressComponents.find((x) =>
    x.types?.includes("sublocality_level_1")
  );

  const area1 = addressComponents.find((x) =>
    x.types?.includes("sublocality_level_2")
  );

  const area2 = addressComponents.find((x) =>
    x.types?.includes("sublocality_level_3")
  );

  const remains: string[] = [];

  const remain1 = addressComponents.find((x) =>
    x.types?.includes("sublocality_level_4")
  );

  const remain1Text = componentLongText(remain1 ?? {});

  if (remain1Text) {
    remains.push(remain1Text);
  }

  const remain2 = addressComponents.find((x) => x.types?.includes("premise"));

  const remain2Text = componentLongText(remain2 ?? {});

  if (remain2Text) {
    remains.push(remain2Text);
  }

  let formattedAddressFmt = options.formattedAddress;

  const countryText = componentLongText(country ?? {});
  const postalCodeText = componentLongText(postalCode ?? {});

  if (country && postalCode && formattedAddressFmt) {
    formattedAddressFmt = formattedAddressFmt.replace(
      `${countryText}、〒${postalCodeText}`,
      ""
    );
  }

  const prefectureFmt = componentLongText(prefecture ?? {});

  const city = `${componentLongText(district ?? {})}${componentLongText(city1 ?? {})}${componentLongText(city2 ?? {})}`;

  const area = `${componentLongText(area1 ?? {})}${componentLongText(area2 ?? {})}`;

  const remain = remains.join("−");

  if (options.regulateArea) {
    try {
      const prefectures = await getPrefectures(false);
      const foundPrefecture = prefectures?.find(
        (x) => x.pref_name === prefectureFmt
      );

      if (foundPrefecture) {
        const cities = await getCities(foundPrefecture.pref_code, false);
        const foundCity = cities?.find((x) => x.city_name === city);

        if (foundCity) {
          const areas = await getAreas(
            foundCity.pref_code,
            foundCity.city_code,
            false
          );

          const areaFmt = `${area}${remain}`;

          for (let i = areaFmt.length; i >= 0; i--) {
            const foundArea = areas?.find((x) => {
              if (areaFmt.substring(0, i) === x.area_name) {
                return true;
              }

              return false;
            });

            if (foundArea) {
              return {
                postalCode,
                formattedAddress: formattedAddressFmt,
                location: options.location,
                name: options.name || "",
                prefecture: foundPrefecture.pref_name,
                city: foundCity.city_name,
                area: foundArea.area_name,
                remain: areaFmt.replace(foundArea.area_name, ""),
                tel: options.tel,
                url: options.url
              };
            }
          }
        }
      }
    } catch {
      // ignore error
    }
  }

  return {
    postalCode,
    formattedAddress: formattedAddressFmt,
    location: options.location,
    name: options.name || "",
    prefecture: prefectureFmt,
    city: city,
    area: area,
    remain: remain,
    tel: options.tel,
    url: options.url
  };
};

export const parsePlace = async (
  place: google.maps.places.Place,
  regulateArea?: boolean
) => {
  await ensurePlaceFields(place);

  const formattedAddress = place.formattedAddress;
  const addressComponents = place.addressComponents;

  if (addressComponents?.length) {
    const location = toLatLngFromPlaceLocation(place.location) ?? undefined;

    return parseAddressComponents(addressComponents, {
      formattedAddress,
      location,
      name: place.displayName ?? "",
      tel: place.nationalPhoneNumber,
      url: place.websiteURI,
      regulateArea
    });
  }

  const location = toLatLngFromPlaceLocation(place.location) ?? undefined;

  return {
    postalCode: undefined,
    formattedAddress,
    location,
    name: place.displayName ?? "",
    prefecture: "",
    city: "",
    area: "",
    remain: "",
    tel: undefined,
    url: undefined
  };
};

export const parseGeocoderResult = async (
  result: google.maps.GeocoderResult,
  regulateArea?: boolean
) => {
  const location = result.geometry?.location ?? undefined;

  if (result.address_components?.length) {
    return parseAddressComponents(result.address_components, {
      formattedAddress: result.formatted_address,
      location,
      name: result.formatted_address,
      regulateArea
    });
  }

  return {
    postalCode: undefined,
    formattedAddress: result.formatted_address,
    location,
    name: result.formatted_address,
    prefecture: "",
    city: "",
    area: "",
    remain: "",
    tel: undefined,
    url: undefined
  };
};
